import styled, { css, keyframes } from 'styled-components';
import { rgba } from 'polished';
import colors from 'constants/colors';

import icCloseBlack from '../../../assets/icons/ic_close_black.svg';

const SIDE = '32px';
const SIDE_MOBILE = '20px';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/** One sheet for both modes, so switching to edit changes the contents, not the frame. */
export const Sheet = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100vw - 32px);
  max-width: 600px;
  max-height: calc(100vh - 80px);
  margin: 40px auto;
  overflow: hidden;
  border-radius: 16px;
  background: ${colors.main.white};
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18);

  @media (max-width: 992px) {
    width: 100vw;
    max-width: none;
    height: 100%;
    max-height: none;
    margin: 0;
    border-radius: 0;
    box-shadow: none;
  }
`;

export const Header = styled.header`
  flex-shrink: 0;
  padding: 24px ${SIDE} 20px;
  border-bottom: 1px solid ${colors.main.dividers};

  @media (max-width: 768px) {
    padding: 16px ${SIDE_MOBILE} 16px;
  }
`;

export const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  margin-bottom: 8px;
`;

export const Eyebrow = styled.span<{ $active?: boolean }>`
  padding: 5px 10px;
  border-radius: 999px;
  background: ${rgba(colors.main.blue, 0.1)};
  color: ${colors.main.darkBlue};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  line-height: 1;
  text-transform: uppercase;

  ${({ $active }) =>
    $active &&
    css`
      background: ${colors.main.black};
      color: ${colors.main.white};
    `}
`;

export const CloseButton = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  margin-left: auto;
  border: none;
  border-radius: 50%;
  background: ${colors.main.dividers} url(${icCloseBlack}) center / 18px no-repeat;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${colors.main.disabledBtns};
  }

  &:focus-visible {
    outline: 2px solid ${colors.main.blue};
    outline-offset: 2px;
  }
`;

const titleType = css`
  font-family: inherit;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 36px;
  color: ${colors.main.black};

  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

export const Title = styled.h1`
  ${titleType}
  margin: 0;
  overflow-wrap: anywhere;
`;

// The typeahead input, restyled to sit exactly where the title was
export const TitleField = styled.div`
  input {
    ${titleType}
    height: 36px;
    padding: 0;
    border: none;
    border-bottom: 2px solid ${colors.main.dividers};
    border-radius: 0;
    transition: border-color 0.15s;

    &:focus {
      border-bottom-color: ${colors.main.blue};
    }

    @media (max-width: 768px) {
      height: 32px;
    }
  }
`;

export const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 24px ${SIDE} 8px;
  animation: ${fadeIn} 0.18s ease-out;

  @media (max-width: 768px) {
    padding: 20px ${SIDE_MOBILE} 8px;
  }
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 28px;
`;

export const StatTile = styled.label`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  min-height: 96px;
  padding: 14px 16px;
  border: 1.5px solid transparent;
  border-radius: 12px;
  background: ${colors.main.secondaryBtns};
  transition: border-color 0.15s;

  &:focus-within {
    border-color: ${colors.main.blue};
  }
`;

export const StatLabel = styled.span`
  color: ${colors.main.grey};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const statValueType = css`
  font-family: inherit;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: ${colors.main.black};
`;

export const StatValue = styled.span`
  ${statValueType}
`;

export const StatSubValue = styled.span`
  margin-left: 6px;
  color: ${colors.main.grey};
  font-size: 14px;
  font-weight: 400;
`;

const bareInput = css`
  ${statValueType}
  width: 100%;
  height: 28px;
  padding: 0;
  border: none;
  outline: none;
  background: none;
`;

export const StatInput = styled.input`
  ${bareInput}

  @media (max-width: 480px) {
    font-size: 16px;
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
  }
`;

export const MinutesRow = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;

  input {
    ${bareInput}
    width: auto;
    min-width: 2ch;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      margin: 0;
      -webkit-appearance: none;
    }
  }
`;

export const Section = styled.section`
  margin-bottom: 28px;
`;

export const SectionLabel = styled.h2`
  margin: 0 0 12px;
  color: ${colors.main.grey};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const Muted = styled.p`
  margin: 0;
  color: ${colors.main.placeholdersAndDisabledBtnsText};
  font-size: 15px;
`;

export const FeltCard = styled.div`
  padding: 14px 16px;
  border-left: 4px solid ${colors.main.blue};
  border-radius: 12px;
  background: ${rgba(colors.main.blue, 0.06)};
`;

export const FeltName = styled.span`
  display: block;
  margin-bottom: 4px;
  color: ${colors.main.black};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const FeltDescription = styled.span`
  display: block;
  color: ${colors.main.grey};
  font-size: 14px;
  line-height: 1.4;
`;

export const FeltOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 10px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const chip = css`
  border: 1.5px solid ${colors.main.black};
  border-radius: 999px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1;
  text-transform: uppercase;
`;

export const FeltOption = styled.button`
  ${chip}
  height: 40px;
  padding: 0 8px;
  background: ${colors.main.white};
  color: ${colors.main.black};
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;

  &[aria-pressed='true'] {
    background: ${colors.main.black};
    color: ${colors.main.white};
  }

  &:focus-visible {
    outline: 2px solid ${colors.main.blue};
    outline-offset: 2px;
  }

  @media (hover: hover) {
    &:not([aria-pressed='true']):hover {
      background: ${colors.main.secondaryBtns};
    }
  }
`;

export const TagList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

// Matches a selected TagPicker option, so a tag looks the same in both modes
export const Tag = styled.li`
  padding: 9px 16px;
  border: 1.5px solid ${colors.main.black};
  border-radius: 999px;
  background: ${colors.main.black};
  color: ${colors.main.white};
  font-size: 15px;
  line-height: 1.2;
`;

export const notesBox = css`
  border: 1.5px solid transparent;
  border-radius: 12px;
  background: ${colors.main.secondaryBtns};
  color: ${colors.main.black};
  font-size: 16px;
  line-height: 1.5;
`;

export const NotesBlock = styled.p`
  ${notesBox}
  margin: 0;
  padding: 14px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const PhotoFrame = styled.div`
  position: relative;
  margin-bottom: 24px;
  overflow: hidden;
  border-radius: 12px;
  background: ${colors.main.dividers};
`;

export const Photo = styled.img`
  display: block;
  width: 100%;
  height: 240px;
  object-fit: cover;

  @media (max-width: 768px) {
    height: 200px;
  }
`;

export const PhotoActions = styled.div`
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: flex;
  gap: 8px;
`;

export const PhotoAction = styled.button`
  height: 36px;
  padding: 0 14px;
  border: none;
  border-radius: 999px;
  background: ${rgba(colors.main.black, 0.75)};
  color: ${colors.main.white};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  backdrop-filter: blur(4px);

  &:hover {
    background: ${colors.main.black};
  }
`;

export const AddPhoto = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 72px;
  margin-bottom: 24px;
  border: 1.5px dashed ${colors.main.disabledBtns};
  border-radius: 12px;
  background: none;
  color: ${colors.main.grey};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition:
    border-color 0.15s,
    color 0.15s;

  &:hover {
    border-color: ${colors.main.blue};
    color: ${colors.main.blue};
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const ErrorText = styled.p`
  margin: 0 0 16px;
  color: ${colors.main.ruby};
  font-size: 14px;
`;

export const Footer = styled.footer`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px ${SIDE};
  border-top: 1px solid ${colors.main.dividers};
  background: ${colors.main.white};

  @media (max-width: 768px) {
    padding: 12px ${SIDE_MOBILE} calc(12px + env(safe-area-inset-bottom));

    button {
      flex: 1;
    }
  }
`;

const footerButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 120px;
  height: 44px;
  padding: 0 22px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;

  &:focus-visible {
    outline: 2px solid ${colors.main.blue};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

export const PrimaryButton = styled.button`
  ${footerButton}
  border: 1.5px solid ${colors.main.black};
  background: ${colors.main.black};
  color: ${colors.main.white};

  @media (hover: hover) {
    &:not(:disabled):hover {
      border-color: ${colors.main.blue};
      background: ${colors.main.blue};
    }
  }
`;

export const SecondaryButton = styled.button`
  ${footerButton}
  border: 1.5px solid ${colors.main.disabledBtns};
  background: ${colors.main.white};
  color: ${colors.main.black};

  @media (hover: hover) {
    &:not(:disabled):hover {
      border-color: ${colors.main.black};
    }
  }
`;

export const PencilIcon = styled.svg`
  width: 14px;
  height: 14px;
  fill: currentColor;
`;
